/**
 * ConditionNode - Read-only branching logic node
 * Shows the needs_kg_search condition decision point
 *
 * NOTE: This node is read-only in the current design.
 * The LLM decides at runtime whether to route to KG search or direct response.
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { ConditionNodeData } from '../../../../stores/workflow/types';

interface ConditionNodeProps {
  data: ConditionNodeData;
  selected?: boolean;
}

export const ConditionNode = memo(({ data, selected }: ConditionNodeProps) => {
  const isReadOnly = data.isReadOnly !== false; // Default to read-only

  return (
    <div
      className={`
        min-w-[180px] rounded-xl border-2 bg-white shadow-lg transition-all
        dark:bg-dark-background-secondary
        ${selected ? 'border-amber-400 ring-4 ring-amber-200/50' : 'border-gray-200 dark:border-border-dark'}
        ${isReadOnly ? 'cursor-not-allowed opacity-90' : ''}
      `}
    >
      {/* Input Handle (top) */}
      <Handle type='target' position={Position.Top} className='!h-3 !w-3 !border-2 !border-white !bg-gray-400' />

      {/* Header - Diamond shape indicator */}
      <div className='flex items-center gap-2 rounded-t-lg bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2'>
        {/* Condition Icon */}
        <div className='text-white'>
          <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>

        <div className='flex-1 font-semibold text-white'>{data.label}</div>

        {isReadOnly && (
          <div className='rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
            <svg className='inline h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m0 0v2m0-2h2m-2 0H9m3-10V5a2 2 0 10-4 0v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2h-4V5a2 2 0 00-4 0' />
            </svg>
          </div>
        )}
      </div>

      {/* Body */}
      <div className='px-4 py-3'>
        {/* Description */}
        {data.description && (
          <div className='mb-2 text-xs text-gray-500 dark:text-dark-text-secondary'>
            {data.description}
          </div>
        )}

        {/* Read-only indicator */}
        {isReadOnly && (
          <div className='rounded-lg bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'>
            LLM decides routing at runtime
          </div>
        )}

        {/* Branch targets (if defined) */}
        {data.branches && (
          <div className='mt-2 space-y-1 text-xs'>
            <div className='flex items-center gap-2'>
              <span className='text-green-600 dark:text-green-400'>True →</span>
              <span className='font-mono text-gray-600 dark:text-dark-text-secondary'>{data.branches.true}</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-red-600 dark:text-red-400'>False →</span>
              <span className='font-mono text-gray-600 dark:text-dark-text-secondary'>{data.branches.false}</span>
            </div>
          </div>
        )}
      </div>

      {/* Output Handles - True (left) and False (right) */}
      <div className='flex items-center justify-between px-4 pb-3'>
        <div className='flex flex-col items-center'>
          <span className='mb-1 text-xs font-medium text-green-600 dark:text-green-400'>True</span>
          <Handle
            type='source'
            position={Position.Bottom}
            id='true'
            className='!relative !left-auto !top-auto !h-3 !w-3 !translate-x-0 !translate-y-0 !border-2 !border-white !bg-green-500'
          />
        </div>
        <div className='flex flex-col items-center'>
          <span className='mb-1 text-xs font-medium text-red-600 dark:text-red-400'>False</span>
          <Handle
            type='source'
            position={Position.Bottom}
            id='false'
            className='!relative !left-auto !top-auto !h-3 !w-3 !translate-x-0 !translate-y-0 !border-2 !border-white !bg-red-500'
          />
        </div>
      </div>
    </div>
  );
});

ConditionNode.displayName = 'ConditionNode';
